import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { collection, doc, serverTimestamp, setDoc, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from './firebase';
import { AuthService } from './auth.service';
import { NotificationService } from './notification';
import { AuthProfile } from './models/auth.model';

@Component({
  selector: 'app-change-profile-photo',
  imports: [CommonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isOpen()) {
      <div
        class="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-xl animate-ios-fade"
        (click)="onBackdropClick($event)"
      >
        <div
          class="relative w-full max-w-lg rounded-[36px] bg-white/90 dark:bg-[#121217]/90 backdrop-blur-2xl border border-white/40 dark:border-white/10 shadow-[0_25px_70px_-15px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col max-h-[92vh] animate-ios-slide"
          (click)="$event.stopPropagation()"
        >
          <!-- Background Glow Accents -->
          <div
            class="absolute -top-24 -left-24 w-56 h-56 bg-ios-blue/20 rounded-full blur-[80px] pointer-events-none"
          ></div>
          <div
            class="absolute -bottom-24 -right-24 w-56 h-56 bg-emerald-500/20 rounded-full blur-[90px] pointer-events-none"
          ></div>

          <!-- Header -->
          <div
            class="relative z-10 px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between bg-white/20 dark:bg-white/[0.02]"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-2xl bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center border border-ios-blue/20 shadow-sm"
              >
                <mat-icon class="!text-[22px]">account_circle</mat-icon>
              </div>
              <div>
                <h2
                  class="text-base sm:text-lg font-black tracking-tight text-slate-900 dark:text-white"
                >
                  Update Profile Photo
                </h2>
                <p class="text-[11px] font-semibold text-slate-500 dark:text-zinc-400">
                  Circular frame crop & 30-day update policy
                </p>
              </div>
            </div>

            <button
              type="button"
              (click)="close()"
              class="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 active:scale-95 transition-all"
              aria-label="Close"
            >
              <mat-icon class="!text-[20px]">close</mat-icon>
            </button>
          </div>

          <!-- Scrollable Body with Hidden Scrollbar -->
          <div
            class="relative z-10 flex-1 overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden p-6 space-y-5"
          >
            <!-- 1. 30-Day Restriction Notice (If Rate Limited) -->
            @if (isRateLimited()) {
              <div
                class="p-4 rounded-3xl bg-amber-500/10 border border-amber-500/25 space-y-2 text-center animate-ios-slide"
              >
                <div class="flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400 font-extrabold text-xs uppercase tracking-wider">
                  <mat-icon class="!text-[18px]">update_disabled</mat-icon>
                  <span>30-Day Update Restriction Active</span>
                </div>
                <p class="text-xs text-slate-700 dark:text-zinc-300 font-medium leading-relaxed">
                  Profile photos can only be changed once every <strong class="font-bold">30 days</strong>.
                  Your last update was on <span class="font-bold">{{ lastChangeDate() | date: 'mediumDate' }}</span>.
                </p>
                <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 text-[11px] font-black">
                  <mat-icon class="!text-[14px]">hourglass_bottom</mat-icon>
                  <span>Available again in {{ daysRemaining() }} days</span>
                </div>
              </div>
            }

            <!-- 2. Interactive Circular Crop Area -->
            <div class="space-y-2">
              <div class="flex items-center justify-between px-1">
                <label
                  class="text-[11px] font-black uppercase tracking-widest text-slate-600 dark:text-zinc-400"
                >
                  Circular Profile Frame
                </label>
                @if (selectedImagePreview()) {
                  <span
                    class="text-[10px] font-mono font-bold text-ios-blue bg-ios-blue/10 px-2 py-0.5 rounded-full"
                  >
                    1:1 Circle
                  </span>
                }
              </div>

              <div
                #dropFrame
                class="relative w-full aspect-square max-w-[280px] mx-auto rounded-[32px] bg-black/95 border-2 border-dashed border-black/10 dark:border-white/15 flex items-center justify-center shadow-inner group overflow-hidden transition-all select-none"
                [class.border-ios-blue]="isDraggingOver()"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)"
              >
                @if (selectedImagePreview(); as preview) {
                  <!-- Draggable & Zoomable Photo in Circular Frame -->
                  <div
                    class="relative w-full h-full overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing"
                    (mousedown)="startPan($event)"
                    (mousemove)="onPan($event)"
                    (mouseup)="endPan()"
                    (mouseleave)="endPan()"
                    (touchstart)="startTouchPan($event)"
                    (touchmove)="onTouchPan($event)"
                    (touchend)="endPan()"
                  >
                    <img
                      [src]="preview"
                      alt="Profile Crop Preview"
                      class="absolute max-w-none origin-center pointer-events-none transition-transform duration-75"
                      [style.transform]="transformStyle()"
                      referrerpolicy="no-referrer"
                    />

                    <!-- Circular Mask Overlay with Rule-of-Thirds Grid -->
                    <div
                      class="absolute inset-0 pointer-events-none rounded-full border-4 border-ios-blue/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] flex items-center justify-center"
                    >
                      <div
                        class="w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 grid grid-cols-3 grid-rows-3 border border-white/20 rounded-full"
                      >
                        <div class="border-r border-b border-white/15"></div>
                        <div class="border-r border-b border-white/15"></div>
                        <div class="border-b border-white/15"></div>
                        <div class="border-r border-b border-white/15"></div>
                        <div class="border-r border-b border-white/15"></div>
                        <div class="border-b border-white/15"></div>
                        <div class="border-r border-white/15"></div>
                        <div class="border-r border-white/15"></div>
                        <div></div>
                      </div>
                    </div>
                  </div>

                  <!-- Frame Actions -->
                  <div class="absolute top-2 right-2 flex items-center gap-1.5 z-20">
                    <button
                      type="button"
                      (click)="fileInput.click()"
                      [disabled]="isRateLimited()"
                      class="px-2.5 py-1 rounded-full bg-black/70 hover:bg-black/90 text-white text-[10px] font-bold border border-white/20 shadow-md flex items-center gap-1 transition-all disabled:opacity-50"
                      title="Replace Photo"
                    >
                      <mat-icon class="!text-[12px]">sync</mat-icon>
                      <span>Replace</span>
                    </button>
                    <button
                      type="button"
                      (click)="removeSelectedImage()"
                      class="w-7 h-7 rounded-full bg-red-500/80 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition-all"
                      title="Remove Photo"
                    >
                      <mat-icon class="!text-[14px]">delete</mat-icon>
                    </button>
                  </div>

                  <!-- Zoom Slider Bar -->
                  <div
                    class="absolute bottom-2 inset-x-2 bg-black/75 backdrop-blur-md rounded-2xl px-3 py-1.5 z-20 flex items-center gap-2 border border-white/10"
                  >
                    <mat-icon class="text-white/60 !text-[14px]">zoom_out</mat-icon>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.05"
                      [value]="zoomScale()"
                      (input)="onZoomChange($event)"
                      class="flex-1 accent-ios-blue h-1 bg-white/20 rounded-lg cursor-pointer"
                    />
                    <mat-icon class="text-white/60 !text-[14px]">zoom_in</mat-icon>
                    <button
                      type="button"
                      (click)="resetTransform()"
                      class="text-[9px] font-bold uppercase text-ios-blue hover:text-white px-1.5 py-0.5 rounded hover:bg-white/10 transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                } @else {
                  <!-- Empty Dropzone -->
                  <div
                    (click)="!isRateLimited() && fileInput.click()"
                    (keydown.enter)="!isRateLimited() && fileInput.click()"
                    tabindex="0"
                    class="flex flex-col items-center justify-center p-6 text-center cursor-pointer hover:bg-white/5 active:scale-98 transition-all w-full h-full"
                    [class.pointer-events-none]="isRateLimited()"
                    [class.opacity-50]="isRateLimited()"
                  >
                    <div
                      class="w-16 h-16 rounded-full bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center mb-3 shadow-sm border border-ios-blue/20"
                    >
                      <mat-icon class="!text-[32px]">add_a_photo</mat-icon>
                    </div>
                    <p class="text-xs font-black text-slate-800 dark:text-white mb-1">
                      Click or drag photo here
                    </p>
                    <p class="text-[11px] text-slate-500 dark:text-zinc-400 max-w-[200px] font-medium leading-normal">
                      Adjust inside circular frame. Supports JPEG, PNG, or WebP.
                    </p>
                    <span
                      class="mt-3 px-3 py-1 rounded-full bg-ios-blue/10 text-ios-blue text-[11px] font-bold border border-ios-blue/20"
                    >
                      Browse Image
                    </span>
                  </div>
                }
              </div>

              <input
                #fileInput
                type="file"
                accept="image/*"
                class="hidden"
                (change)="onFileSelected($event)"
              />
            </div>

            <!-- 3. Instructions & Copyright Warnings Card -->
            <div
              class="p-4 rounded-3xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/10 space-y-3"
            >
              <div class="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                <mat-icon class="text-ios-blue !text-[18px]">verified_user</mat-icon>
                <span>Profile Photo Guidelines & Policy</span>
              </div>

              <div class="space-y-2 text-xs text-slate-600 dark:text-zinc-300 font-medium leading-relaxed">
                @if (accountType() === 'person') {
                  <div class="p-2.5 rounded-2xl bg-ios-blue/10 text-ios-blue dark:text-ios-blue-dark flex items-start gap-2 border border-ios-blue/20">
                    <mat-icon class="!text-[16px] shrink-0 mt-0.5">face</mat-icon>
                    <span>
                      <strong class="font-bold">Personal Account:</strong> Must feature your clear, real human face. Photos of animals, random objects, or missing faces will be rejected.
                    </span>
                  </div>
                } @else {
                  <div class="p-2.5 rounded-2xl bg-purple-500/10 text-purple-600 dark:text-purple-300 flex items-start gap-2 border border-purple-500/20">
                    <mat-icon class="!text-[16px] shrink-0 mt-0.5">business</mat-icon>
                    <span>
                      <strong class="font-bold">Organization Account:</strong> Must feature your official authorized, copyrighted, or trademarked company logo/brandmark.
                    </span>
                  </div>
                }

                <div class="p-2.5 rounded-2xl bg-red-500/10 text-red-600 dark:text-red-400 flex items-start gap-2 border border-red-500/20">
                  <mat-icon class="!text-[16px] shrink-0 mt-0.5">warning</mat-icon>
                  <span>
                    <strong class="font-bold">Admin Warning:</strong> All submissions undergo administrative review. Unauthorized, copyrighted, or inappropriate content will be rejected and deleted by administrators.
                  </span>
                </div>
              </div>
            </div>

            <!-- Error Message Feedback -->
            @if (errorMessage()) {
              <div
                class="p-3 rounded-2xl bg-red-500/10 border border-red-500/20 text-xs font-bold text-red-500 flex items-center gap-2"
              >
                <mat-icon class="!text-[16px]">error</mat-icon>
                <span>{{ errorMessage() }}</span>
              </div>
            }

            <!-- Footer Action Buttons -->
            <div class="pt-2 flex items-center gap-3">
              <button
                type="button"
                (click)="close()"
                class="flex-1 h-12 rounded-2xl font-bold text-xs uppercase tracking-wider text-slate-600 dark:text-zinc-300 hover:bg-black/5 dark:hover:bg-white/5 active:scale-95 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                (click)="submitPhotoUpdate()"
                [disabled]="isSubmitting() || !selectedFile() || isRateLimited()"
                class="flex-[2] h-12 rounded-2xl font-black text-xs uppercase tracking-wider bg-ios-blue text-white shadow-ios shadow-ios-blue/25 hover:bg-ios-blue/95 active:scale-98 transition-all disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center gap-2"
              >
                @if (isSubmitting()) {
                  <div
                    class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                  ></div>
                  <span>Submitting...</span>
                } @else {
                  <mat-icon class="!text-[18px]">cloud_upload</mat-icon>
                  <span>Submit for Review</span>
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class ChangeProfilePhoto {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);

  @ViewChild('dropFrame') dropFrame?: ElementRef<HTMLDivElement>;

  isOpen = input<boolean>(false);
  currentProfile = input<AuthProfile | null>(null);
  closed = output<void>();
  photoUpdated = output<void>();

  selectedFile = signal<File | null>(null);
  selectedImagePreview = signal<string | null>(null);

  isDraggingOver = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  // 30-Day Restriction Signals
  isRateLimited = signal<boolean>(false);
  daysRemaining = signal<number>(0);
  lastChangeDate = signal<Date | null>(null);

  // Transform state for circular crop
  panX = signal<number>(0);
  panY = signal<number>(0);
  zoomScale = signal<number>(1);
  private isPanning = false;
  private startMouseX = 0;
  private startMouseY = 0;
  private initialPanX = 0;
  private initialPanY = 0;

  accountType = computed(() => this.currentProfile()?.accountType || 'person');

  transformStyle = computed(() => {
    return `translate(${this.panX()}px, ${this.panY()}px) scale(${this.zoomScale()})`;
  });

  constructor() {
    effect(() => {
      if (this.isOpen() && this.currentProfile()) {
        this.check30DayRestriction();
      }
    });
  }

  async check30DayRestriction() {
    const profile = this.currentProfile();
    if (!profile?.uid) return;

    try {
      // Check lastPhotoChangeTimestamp or profilePhoto subcollection
      let lastTimestamp: number | null = null;

      if (profile.lastPhotoChange) {
        lastTimestamp = typeof profile.lastPhotoChange === 'object' && 'toMillis' in profile.lastPhotoChange
          ? (profile.lastPhotoChange as any).toMillis()
          : new Date(profile.lastPhotoChange as any).getTime();
      }

      if (!lastTimestamp) {
        // Query profilePhoto subcollection for latest entry
        const subCol = collection(db, 'accounts', profile.uid, 'profilePhoto');
        const q = query(subCol, orderBy('createdAt', 'desc'), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const data = snap.docs[0].data();
          if (data['createdAt'] && typeof data['createdAt'].toMillis === 'function') {
            lastTimestamp = data['createdAt'].toMillis();
          }
        }
      }

      if (lastTimestamp) {
        const now = Date.now();
        const diffMs = now - lastTimestamp;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays < 30) {
          this.isRateLimited.set(true);
          this.daysRemaining.set(30 - diffDays);
          this.lastChangeDate.set(new Date(lastTimestamp));
        } else {
          this.isRateLimited.set(false);
          this.daysRemaining.set(0);
        }
      } else {
        this.isRateLimited.set(false);
      }
    } catch (err) {
      console.warn('Could not verify 30-day photo update rate limit:', err);
      this.isRateLimited.set(false);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isRateLimited()) this.isDraggingOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver.set(false);
    if (!this.isRateLimited() && event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      this.errorMessage.set('Please select a valid image file.');
      return;
    }

    this.errorMessage.set(null);
    this.selectedFile.set(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      this.selectedImagePreview.set(e.target?.result as string);
      this.resetTransform();
    };
    reader.readAsDataURL(file);
  }

  removeSelectedImage() {
    this.selectedFile.set(null);
    this.selectedImagePreview.set(null);
    this.resetTransform();
  }

  resetTransform() {
    this.panX.set(0);
    this.panY.set(0);
    this.zoomScale.set(1);
  }

  onZoomChange(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    this.zoomScale.set(value);
  }

  startPan(event: MouseEvent) {
    this.isPanning = true;
    this.startMouseX = event.clientX;
    this.startMouseY = event.clientY;
    this.initialPanX = this.panX();
    this.initialPanY = this.panY();
  }

  onPan(event: MouseEvent) {
    if (!this.isPanning) return;
    const dx = event.clientX - this.startMouseX;
    const dy = event.clientY - this.startMouseY;
    this.panX.set(this.initialPanX + dx);
    this.panY.set(this.initialPanY + dy);
  }

  startTouchPan(event: TouchEvent) {
    if (event.touches.length === 1) {
      this.isPanning = true;
      this.startMouseX = event.touches[0].clientX;
      this.startMouseY = event.touches[0].clientY;
      this.initialPanX = this.panX();
      this.initialPanY = this.panY();
    }
  }

  onTouchPan(event: TouchEvent) {
    if (!this.isPanning || event.touches.length !== 1) return;
    const dx = event.touches[0].clientX - this.startMouseX;
    const dy = event.touches[0].clientY - this.startMouseY;
    this.panX.set(this.initialPanX + dx);
    this.panY.set(this.initialPanY + dy);
  }

  endPan() {
    this.isPanning = false;
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  close() {
    this.errorMessage.set(null);
    this.isSubmitting.set(false);
    this.removeSelectedImage();
    this.closed.emit();
  }

  async submitPhotoUpdate() {
    const user = this.authService.user();
    const profile = this.currentProfile();
    const file = this.selectedFile();

    if (!user || !profile || !file) {
      this.errorMessage.set('Please select an image file.');
      return;
    }

    if (this.isRateLimited()) {
      this.errorMessage.set(`Profile photo can only be updated once every 30 days. Available in ${this.daysRemaining()} days.`);
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `accounts/${user.uid}/profilePhotos/${timestamp}_${safeName}`;
      const fileRef = ref(storage, storagePath);

      // Upload file to Firebase Storage
      await uploadBytes(fileRef, file, {
        contentType: file.type || 'image/jpeg',
      });
      const downloadUrl = await getDownloadURL(fileRef);

      // Save to accounts/{uid}/profilePhoto subcollection
      const subCol = collection(db, 'accounts', user.uid, 'profilePhoto');
      const docRef = doc(subCol);

      const photoRecord = {
        id: docRef.id,
        uid: user.uid,
        imageUrl: downloadUrl,
        originalPhotoURL: profile.photoURL || profile.photo || '',
        accountType: profile.accountType || 'person',
        displayName: profile.displayName || '',
        username: profile.username || '',
        email: profile.email || '',
        status: 'pending',
        verificationStatus: 'pending',
        createdAt: serverTimestamp(),
        storagePath: storagePath,
        panNumber: profile.documentNumber || '',
        panName: profile.displayName || '',
      };

      await setDoc(docRef, photoRecord);

      this.notificationService.success(
        'New profile photo submitted for administrative review!'
      );
      this.photoUpdated.emit();
      this.close();
    } catch (err: unknown) {
      console.error('Error submitting profile photo:', err);
      const msg = err instanceof Error ? err.message : 'Failed to submit profile photo';
      this.errorMessage.set(msg);
      this.notificationService.error(msg);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
